import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

interface LabelSheetStartPickerProps {
	columns: number;
	rows: number;
	startColumn: number;
	startRow: number;
	onChange: (startColumn: number, startRow: number) => void;
}

export default function LabelSheetStartPicker({
	columns,
	rows,
	startColumn,
	startRow,
	onChange,
}: LabelSheetStartPickerProps) {
	const startIndex = startRow * columns + startColumn;

	return (
		<Box>
			<Typography variant="body2" gutterBottom>
				Select starting label position (tap the first empty label on your sheet):
			</Typography>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
					gap: "4px",
					width: "100%",
					"& > *": {
						minWidth: 0,
					},
					// Desktop: cap width so the grid stays compact and all rows stay visible
					maxWidth: { sm: `${columns * 52}px` },
				}}
			>
				{Array.from({ length: rows * columns }).map((_, index) => {
					const col = index % columns;
					const row = Math.floor(index / columns);
					const isUsed = index < startIndex;
					const isSelected = index === startIndex;

					return (
						<Tooltip key={index} title={isUsed ? "Used" : isSelected ? "Start here" : "Available"}>
							<Box
								onClick={() => onChange(col, row)}
								sx={{
									width: "100%",
									boxSizing: "border-box",
									// Mobile: proportional aspect ratio with minimum tap target
									aspectRatio: { xs: "2 / 1" },
									minHeight: { xs: 44, sm: 0 },
									// Desktop: fixed compact height so all rows fit without scrolling
									height: { sm: 30 },
									border: "1.5px solid",
									borderColor: isSelected ? "#1976d2" : "grey.400",
									backgroundColor: isUsed ? "grey.300" : isSelected ? "#bbdefb" : "background.paper",
									cursor: "pointer",
									borderRadius: "3px",
									"&:hover": {
										borderColor: isUsed ? "primary.main" : "#1976d2",
										backgroundColor: isUsed ? "grey.400" : isSelected ? "#90caf9" : "primary.light",
									},
									"&:active": {
										transform: "scale(0.97)",
									},
								}}
							/>
						</Tooltip>
					);
				})}
			</Box>
			<Typography variant="caption" sx={{ mt: 1, display: "block" }}>
				Starting at row {startRow + 1}, column {startColumn + 1}
			</Typography>
		</Box>
	);
}
